import {
  getCachedCouponRuleByCode,
  saveCouponRuleToReferenceCache,
} from "./reference-cache";
import {
  type ServerCouponLike,
  toCachedCouponRuleRecord,
  validateCouponFromCache,
} from "./validate-coupon-from-cache";

export type ResolveLocalFirstCouponResult = {
  valid: boolean;
  code: string;
  calculatedDiscount?: number;
  coupon?: {
    id: string;
    discountType: "PERCENT" | "AMOUNT";
    discountValue: number;
  };
  error?: string;
  validationSource: "network" | "cache";
  needsServerConfirmation: boolean;
};

type ResolveLocalFirstCouponInput = {
  branchId: string;
  deviceId?: string;
  code: string;
  customerId?: string;
  baseAmount: number;
  orderType?: "DINE_IN" | "TAKEAWAY" | "DELIVERY";
  validateOnline: () => Promise<{
    valid: boolean;
    coupon?: ServerCouponLike;
    calculatedDiscount?: number;
    error?: string;
  }>;
};

function canUseNetwork(): boolean {
  return typeof navigator === "undefined" || navigator.onLine;
}

export async function resolveLocalFirstCouponValidation(
  input: ResolveLocalFirstCouponInput,
): Promise<ResolveLocalFirstCouponResult> {
  const normalizedCode = input.code.trim().toUpperCase();
  if (!normalizedCode) {
    return {
      valid: false,
      code: normalizedCode,
      error: "Coupon code is required",
      validationSource: "cache",
      needsServerConfirmation: false,
    };
  }

  if (canUseNetwork()) {
    try {
      const result = await input.validateOnline();
      if (
        result.valid &&
        result.coupon != null &&
        result.calculatedDiscount != null
      ) {
        await saveCouponRuleToReferenceCache(input.branchId, result.coupon, {
          deviceId: input.deviceId,
        });

        return {
          valid: true,
          code: normalizedCode,
          calculatedDiscount: result.calculatedDiscount,
          coupon: {
            id: result.coupon.id,
            discountType: result.coupon.discountType,
            discountValue: Number(result.coupon.discountValue),
          },
          validationSource: "network",
          needsServerConfirmation: false,
        };
      }

      return {
        valid: false,
        code: normalizedCode,
        error: result.error ?? "ໂຄດສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ",
        validationSource: "network",
        needsServerConfirmation: false,
      };
    } catch {
      // fall through to cache when network fails mid-request
    }
  }

  const cachedRule = await getCachedCouponRuleByCode(
    input.branchId,
    normalizedCode,
  );
  if (!cachedRule) {
    return {
      valid: false,
      code: normalizedCode,
      error: canUseNetwork()
        ? "ໂຄດສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ"
        : "ບໍ່ພົບ coupon ໃນ cache — ກະລຸນາເຊື່ອມຕໍ່ອິນເຕີເນັດ ຫຼື refresh cache",
      validationSource: "cache",
      needsServerConfirmation: false,
    };
  }

  const cacheResult = validateCouponFromCache({
    rule: cachedRule,
    customerId: input.customerId,
    baseAmount: input.baseAmount,
    orderType: input.orderType,
  });

  if (!cacheResult.valid || cacheResult.calculatedDiscount == null) {
    return {
      valid: false,
      code: normalizedCode,
      error: cacheResult.error ?? "ໂຄດສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ",
      validationSource: "cache",
      needsServerConfirmation: cacheResult.needsServerConfirmation,
    };
  }

  return {
    valid: true,
    code: normalizedCode,
    calculatedDiscount: cacheResult.calculatedDiscount,
    coupon: cacheResult.coupon
      ? {
          id: cacheResult.coupon.id,
          discountType: cacheResult.coupon.discountType,
          discountValue: cacheResult.coupon.discountValue,
        }
      : undefined,
    validationSource: "cache",
    needsServerConfirmation: cacheResult.needsServerConfirmation,
  };
}

export { toCachedCouponRuleRecord };
